import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Grid,
    Box,
    Typography,
    TextField,
    MenuItem
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { Controller, useForm } from 'react-hook-form';
import Editor from 'ui-component/QuillEditor/Editor';
import { post, put, get } from '../../axios/apiService';
import toast from 'react-hot-toast';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

function AddEditTemplateForm({ title = "Email Template" }) {
    const navigate = useNavigate();
    const { id } = useParams(); // route param id (if editing)
    const editorRef = useRef(null); // holds the Quill instance
    const [selectedVariableIds, setSelectedVariableIds] = useState([]);


    const [variables, setVariables] = useState([]); // [{id, key}, ...]
    const [selectedVar, setSelectedVar] = useState(""); // show briefly in dropdown
    const [isLoading, setIsLoading] = useState(false);
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Template);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            Name: "",
            description: ""
        },
        mode: "onChange"
    });

    // Load variables once
    useEffect(() => {
        const loadVariables = async () => {
            try {
                const response = await get("/EmailTemplate/Variables");
                if (response?.result && Array.isArray(response.result)) {
                    setVariables(response.result);
                }
            } catch (err) {
                console.error("Failed to load variables:", err);
                toast.error("Error loading variables");
            }
        };
        loadVariables();
    }, []);

    // Load template for edit
    useEffect(() => {
        if (!id) return;
        const loadTemplate = async () => {
            setIsLoading(true);
            try {
                const response = await get(`EmailTemplate/${id}`);

                if (response?.result) {
                    reset({
                        Name: response.result.name || "",
                        description: response.result.description || ""
                    });

                    // ✅ Load saved template variables
                    if (Array.isArray(response.result.emailTemplateVariables)) {
                        setSelectedVariableIds(
                            response.result.emailTemplateVariables.map(v => v.variableId)
                        );
                    }
                }
            } catch (err) {
                console.error("Failed to load template:", err);
                toast.error("Failed to load template");
            } finally {
                setIsLoading(false);
            }
        };
        loadTemplate();
    }, [id, reset]);


    // Called by Editor when Quill is ready
    const handleEditorReady = (quillInstance) => {
        editorRef.current = quillInstance;
    };

    // Insert variable into editor and update RHF value
    const insertVariable = (key) => {
        const quill = editorRef.current;
        if (!quill) {
            console.warn("Quill not ready yet");
            return;
        }

        const tag = `{{${key}}}`;

        try {
            quill.focus();

            // Determine insertion index: caret index or end
            const sel = quill.getSelection();
            const index = (sel && typeof sel.index === 'number') ? sel.index : quill.getLength();

            // Use 'user' source so events propagate
            quill.insertText(index, tag, 'user');

            // Move cursor after inserted tag
            quill.setSelection(index + tag.length, 0);

            // Update react-hook-form description field with current HTML
            const html = quill.root && quill.root.innerHTML ? quill.root.innerHTML : '';
            setValue('description', html, { shouldDirty: true, shouldValidate: true });

        } catch (err) {
            console.error("Error inserting variable:", err);
        }
    };

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            // Ensure description is up-to-date
            if (editorRef.current) {
                const html = editorRef.current.root.innerHTML;
                data.description = html;
            }

            const payload = {
                Name: data.Name,
                Description: data.description,
                EmailTemplateVariables: selectedVariableIds.map(id => ({
                    VariableId: id
                }))
            };

            if (id) {
                await put(`EmailTemplate/${id}`, payload);
                toast.success("Template updated successfully");
            } else {
                await post("EmailTemplate", payload);
                toast.success("Template created successfully");
            }

            navigate("/marketing/template");

        } catch (err) {
            console.error("Save error:", err);
            const msg = err?.response?.data || err?.message || "Error saving template";
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const extractVariablesFromHtml = (html) => {
        const regex = /\{\{([^}]+)\}\}/g;
        const matches = [...html.matchAll(regex)];
        return matches.map(m => m[1]);
    };



    return (
        <div>
            <MainCard title={title} backButton={{ onClick: () => navigate('/marketing/template') }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>

                        {/* Template Name */}
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="Name"
                                control={control}
                                rules={{ required: "Template name is required" }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Template Name *"
                                        error={!!errors.Name}
                                        helperText={errors.Name?.message}
                                        disabled={!canEdit}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Variable dropdown */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Insert Variable"
                                value={selectedVar}
                                disabled={!canEdit}
                                onChange={(e) => {
                                    const key = e.target.value;
                                    if (!key) return;

                                    // 1️⃣ Insert into editor
                                    insertVariable(key);

                                    // 2️⃣ Find variable ID from its key
                                    const selected = variables.find(v => v.key === key);
                                    if (selected) {
                                        setSelectedVariableIds(prev => {
                                            // ensure unique
                                            if (!prev.includes(selected.id)) {
                                                return [...prev, selected.id];
                                            }
                                            return prev;
                                        });
                                    }

                                    // 3️⃣ Reset dropdown after small delay
                                    setSelectedVar(key);
                                    setTimeout(() => setSelectedVar(""), 500);
                                }}
                            >
                                <MenuItem value="" disabled={!canEdit}>
                                    -- Select Variable --
                                </MenuItem>

                                {variables.map(v => (
                                    <MenuItem key={v.id} value={v.key} disabled={!canEdit}>
                                        {v.key}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>


                        {/* Editor */}
                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    required: "Description is required",
                                    validate: val => (val && val.trim() !== "" && val !== '<p><br></p>') || "Content cannot be empty"
                                }}
                                render={({ field }) => (
                                    <Box>
                                        <Editor
                                            value={field.value}
                                            readOnly={!canEdit}
                                            onChange={(html) => {
                                                field.onChange(html);

                                                // Extract keys from HTML
                                                const keys = extractVariablesFromHtml(html);

                                                // Convert key → variableId using your variables array
                                                const variableIdsInEditor = variables
                                                    .filter(v => keys.includes(v.key))
                                                    .map(v => v.id);

                                                // Set selectedVariableIds to match what's inside editor
                                                setSelectedVariableIds(variableIdsInEditor);
                                            }}

                                            onReady={handleEditorReady}
                                            style={{ minHeight: 180 }}
                                        />
                                        {errors.description && (
                                            <Typography color="error" variant="caption">
                                                {errors.description.message}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        </Grid>

                        {/* Save */}
                        {
                            canEdit && (
                                <Grid item xs={12}>
                                    <Button type="submit" variant="contained" sx={{ mt: 2, px: 4 }} disabled={!canEdit}>
                                        Save
                                    </Button>
                                </Grid>
                            )
                        }

                    </Grid>
                </form>
            </MainCard>

            <LoadingBackdrop open={isLoading} />
        </div>
    );
}

export default AddEditTemplateForm;
